import type { CSSProperties } from "react";
import { Icon } from "../components/Icon";
import type { LatestPlanSnapshot, PlanDay, RecoveryMetricsMap, User, WearableConnection, WorkoutSession } from "../types";
import { aggregateRecoveryMetrics, completedPlanDates, formatDate, formatDuration, localDateKey, todayPlanDay } from "../utils";

interface WorkoutControls {
  active: WorkoutSession | null;
  elapsed: number;
  selectedRpe: number | null;
  setSelectedRpe: (value: number) => void;
  start: (day: PlanDay) => Promise<WorkoutSession | null>;
  pause: () => Promise<WorkoutSession | null>;
  resume: () => Promise<WorkoutSession | null>;
  finish: () => Promise<WorkoutSession | null>;
  cancel: () => Promise<WorkoutSession | null>;
}

interface DashboardPageProps {
  user: User;
  snapshot: LatestPlanSnapshot | null;
  sessions: WorkoutSession[];
  wearables: WearableConnection[];
  metrics: RecoveryMetricsMap;
  workout: WorkoutControls;
  onCreatePlan: () => void;
  onManageRecovery: () => void;
  onConfirmCancel: () => Promise<boolean>;
}

export function DashboardPage({
  user, snapshot, sessions, wearables, metrics, workout,
  onCreatePlan, onManageRecovery, onConfirmCancel,
}: DashboardPageProps) {
  const day = todayPlanDay(snapshot);
  const completed = completedPlanDates(sessions);
  const recovery = aggregateRecoveryMetrics(wearables, metrics);
  const currentWeek = snapshot?.plan.weekly_schedule?.find((week) => week.days.some((item) => item.date === localDateKey()))
    || snapshot?.plan.weekly_schedule?.[0];
  const planned = currentWeek?.days.filter((item) => item.type !== "Rest") || [];
  const completedCount = planned.filter((item) => completed.has(item.date)).length;
  const adherence = planned.length ? Math.round(completedCount / planned.length * 100) : 0;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "GOOD MORNING" : hour < 17 ? "GOOD AFTERNOON" : "GOOD EVENING";

  async function discard() {
    if (await onConfirmCancel()) await workout.cancel();
  }

  return (
    <section className="route active-route">
      <header className="page-header">
        <div><p className="eyebrow">{greeting}</p><h1>{user.name || "Athlete"}</h1></div>
        <div className="date-chip">{formatDate(new Date(), { weekday: "long", month: "long", day: "numeric" })}</div>
      </header>

      {workout.active ? (
        <section className="live-workout" aria-live="polite">
          <div className="live-layout">
            <div>
              <div className="live-title">
                <span className="signal-dot" />
                <div>
                  <p className="eyebrow">LIVE WORKOUT</p>
                  <h2>{workout.active.title || workout.active.workout_type || "Workout"}</h2>
                  <span className="live-source">{workout.active.source} · {workout.active.status}</span>
                </div>
              </div>
              <div className="live-actions">
                <button className={`button ${workout.active.status === "paused" ? "primary" : "secondary"}`} type="button" onClick={() => void (workout.active?.status === "paused" ? workout.resume() : workout.pause())}>
                  <Icon name={workout.active.status === "paused" ? "play" : "pause"} />
                  {workout.active.status === "paused" ? "Resume" : "Pause"}
                </button>
                <button className="button primary" type="button" onClick={() => void workout.finish()}><Icon name="check" />Finish workout</button>
                <button className="button text danger-text" type="button" onClick={() => void discard()}>Discard</button>
              </div>
              <div className="rpe-group"><span>Effort</span>{Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
                <button key={value} className={`rpe-button ${workout.selectedRpe === value ? "selected" : ""}`} type="button" onClick={() => workout.setSelectedRpe(value)} aria-label={`RPE ${value}`}>{value}</button>
              ))}</div>
            </div>
            <div className="timer">{formatDuration(workout.elapsed)}</div>
          </div>
        </section>
      ) : null}

      <div className="dashboard-grid">
        <section className="card workout-card">
          {day ? (
            <>
              <p className="eyebrow">{day.date === localDateKey() ? "TODAY'S WORKOUT" : "NEXT WORKOUT"}</p>
              <h2>{day.title || day.type}</h2>
              <div className="workout-meta">
                {day.duration_minutes ? <span className="pill">{day.duration_minutes} min</span> : null}
                {day.intensity ? <span className="pill warm">{day.intensity}</span> : null}
                {day.distance ? <span className="pill">{day.distance.value} {day.distance.unit}</span> : null}
                {completed.has(day.date) ? <span className="pill success">Completed</span> : null}
              </div>
              <p className="card-copy">{day.workout || day.notes || "Follow your planned session."}</p>
              {day.form_cues?.length ? <div className="cue-list"><strong>Form cues</strong>{day.form_cues.map((cue) => <span key={cue}>{cue}</span>)}</div> : null}
              <div className="card-actions">
                <button className="button primary" type="button" disabled={Boolean(workout.active) || completed.has(day.date) || day.type === "Rest"} onClick={() => void workout.start(day)}>
                  <Icon name="play" />{completed.has(day.date) ? "Completed" : day.type === "Rest" ? "Recovery day" : "Start workout"}
                </button>
              </div>
            </>
          ) : (
            <div className="empty-state"><div><h2>No workout available</h2><p>Create your first personalized plan.</p><button className="button primary" type="button" onClick={onCreatePlan}>Create plan</button></div></div>
          )}
        </section>

        <section className="card overview-card">
          <p className="eyebrow">PLAN OVERVIEW</p>
          {snapshot ? (
            <>
              <h2>{snapshot.plan.training_plan?.phases?.[0]?.name || "Personalized training"}</h2>
              <p className="card-copy">{snapshot.plan.training_plan?.overview || "Your current training block."}</p>
              <div className="workout-meta">
                <span className="pill">{snapshot.plan.training_plan?.total_weeks || snapshot.plan.weekly_schedule?.length || 0} weeks</span>
                <span className="pill warm">{snapshot.plan.user_summary?.goals?.join(" · ") || snapshot.profile.goals.join(" · ")}</span>
              </div>
              {snapshot.plan.adaptation_reason ? <div className="notice"><strong>Why it changed</strong><span>{snapshot.plan.adaptation_reason}</span></div> : null}
            </>
          ) : <div className="empty-state"><p>No saved plan yet.</p></div>}
        </section>

        <section className="card metric-card">
          <div className="progress-ring" style={{ "--progress": `${adherence * 3.6}deg` } as CSSProperties}><span>{planned.length ? `${adherence}%` : "—"}</span></div>
          <div><p className="muted">Weekly adherence</p><strong>{completedCount}/{planned.length}</strong><small>sessions completed</small></div>
        </section>

        <section className="card recovery-summary">
          <div className="section-title"><div><p className="eyebrow">RECOVERY</p><h2>Latest signals</h2></div><button className="button text" type="button" onClick={onManageRecovery}>Manage</button></div>
          <div className="stat-grid">
            {recovery.connectedSources.length ? (
              <>
                <Stat label="Readiness" value={recovery.readinessScore == null ? "—" : `${recovery.readinessScore}/100`} />
                <Stat label="Sleep" value={recovery.avgSleepHours == null ? "—" : `${recovery.avgSleepHours.toFixed(1)} h`} />
                <Stat label="HRV" value={recovery.avgHRV == null ? "—" : `${Math.round(recovery.avgHRV)} ms`} />
                <Stat label="Resting HR" value={recovery.restingHR == null ? "—" : `${Math.round(recovery.restingHR)} bpm`} />
              </>
            ) : <div className="empty-inline">Connect a wearable to add recovery context.</div>}
          </div>
        </section>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="stat"><span>{label}</span><strong>{value}</strong></div>;
}
